import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import { useBlog, useBlogs } from "../../../services/blogService";
import type { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";

const BlogDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const isDark = useSelector((state: RootState) => state.theme.darkMode);

  const blogId = id ? parseInt(id) : 0;
  const { data: post, isLoading } = useBlog(blogId, false, blogId > 0);
  const { data: relatedBlogsData } = useBlogs({
    locale: currentLanguage,
    status: "published",
    per_page: 3,
  });

  const relatedPosts = relatedBlogsData?.posts.filter((p) => p.id !== id).slice(0, 2) || [];

  if (isLoading) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <Header />
        <main className="py-16 px-4 sm:px-16 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (!post) {
    return (
      <div
        className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
          }`}
      >
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-light mb-4">
              {t("blog.postNotFound")}
            </h1>
            <p
              className={`text-lg mb-8 ${isDark ? "text-gray-400" : "text-gray-600"
                }`}
            >
              {t("blog.postNotFoundText")}
            </p>
            <button
              onClick={() => navigate(`/${currentLanguage}/blogs`)}
              className={`px-6 py-3 rounded-none font-light tracking-wide transition-colors ${isDark
                  ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
                  : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
            >
              {t("blog.backToBlogs")}
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
        }`}
    >
      <Header />
      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`relative h-96 md:h-[60vh] overflow-hidden ${isDark ? "bg-gray-800" : "bg-gray-100"
            }`}
        >
          {post.featured_image ? (
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-400">No image</span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="max-w-4xl"
            >
              <div className="mb-4">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-block bg-white/90 text-gray-900 px-3 py-1 text-sm font-medium tracking-wide">
                    {post.categories[0]}
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-5xl font-light text-white mb-4 leading-tight">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed max-w-2xl">
                  {post.excerpt}
                </p>
              )}
            </motion.div>
          </div>
        </motion.section>

        {/* Article Content */}
        <motion.article
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-4xl mx-auto">
            {/* Article Meta */}
            <div
              className={`flex flex-wrap items-center justify-between mb-12 pb-8 border-b ${isDark ? "border-gray-700" : "border-gray-200"
                }`}
            >
              <div className="flex items-start space-x-6">
                {post.author_image && (
                  <img
                    src={post.author_image}
                    alt={post.author || ""}
                    className="w-16 h-16 rounded-full object-cover shadow-md"
                  />
                )}
                <div>
                  <p
                    className={`text-lg font-medium leading-tight ${isDark ? "text-gray-100" : "text-gray-900"
                      }`}
                  >
                    {post.author || "Admin"}
                  </p>
                  {post.author_title && (
                    <p
                      className={`text-sm font-light mt-0.5 ${isDark ? "text-blue-400" : "text-blue-600"
                        }`}
                    >
                      {post.author_title}
                    </p>
                  )}
                  <p
                    className={`text-xs mt-2 uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                  >
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                      : ""}
                  </p>
                  {post.author_bio && (
                    <p
                      className={`text-sm mt-4 font-light leading-relaxed max-w-lg italic ${isDark ? "text-gray-400" : "text-gray-600"
                        }`}
                    >
                      "{post.author_bio}"
                    </p>
                  )}
                </div>
              </div>
              <div
                className={`flex items-center space-x-4 text-sm ${isDark ? "text-gray-400" : "text-gray-600"
                  }`}
              >
                <span>
                  {post.read_time || 5} {t("blog.minRead")}
                </span>
                {post.tags && post.tags.length > 0 && (
                  <div className="flex space-x-2">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-1 rounded text-xs ${isDark
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-700"
                          }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Article Body */}
            <div className="prose prose-lg max-w-none font-light leading-relaxed">
              <div
                className={isDark ? "text-gray-300" : "text-gray-800"}
                dangerouslySetInnerHTML={{
                  __html: post.body || "",
                }}
              />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div
                className={`mt-12 pt-8 border-t ${isDark ? "border-gray-700" : "border-gray-200"
                  }`}
              >
                <h3
                  className={`text-lg font-medium mb-4 ${isDark ? "text-gray-100" : "text-gray-900"
                    }`}
                >
                  {t("blog.tags")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-sm font-light tracking-wide hover transition-colors cursor-pointer ${isDark
                          ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                        }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className={`py-16 px-4 sm:px-16 ${isDark ? "bg-gray-800" : "bg-gray-50"
              }`}
          >
            <div className="max-w-6xl mx-auto">
              <h2
                className={`text-2xl md:text-3xl font-light text-center mb-12 ${isDark ? "text-gray-100" : "text-gray-900"
                  }`}
              >
                {t("blog.relatedArticles")}
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <motion.article
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: Math.min(relatedPosts.indexOf(relatedPost) * 0.1, 1) }}
                    className={`p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer ${isDark ? "bg-gray-700 hover:bg-gray-600" : "bg-white"
                      }`}
                    onClick={() =>
                      navigate(`/${currentLanguage}/blogs/${relatedPost.id}`)
                    }
                  >
                    {relatedPost.featured_image ? (
                      <img
                        src={relatedPost.featured_image}
                        alt={relatedPost.title}
                        className="w-full h-48 object-cover rounded mb-4"
                      />
                    ) : (
                      <div className="w-full h-48 bg-gray-200 rounded mb-4 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No image</span>
                      </div>
                    )}
                    <div className="mb-2">
                      {relatedPost.categories && relatedPost.categories.length > 0 && (
                        <span
                          className={`text-xs font-medium uppercase tracking-wide ${isDark ? "text-gray-400" : "text-gray-500"
                            }`}
                        >
                          {relatedPost.categories[0]}
                        </span>
                      )}
                    </div>
                    <h3
                      className={`text-lg font-medium mb-2 leading-tight ${isDark ? "text-gray-100" : "text-gray-900"
                        }`}
                    >
                      {relatedPost.title}
                    </h3>
                    <p
                      className={`text-sm font-light leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                    >
                      {relatedPost.excerpt}
                    </p>
                    <div
                      className={`mt-4 flex items-center justify-between text-xs ${isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                    >
                      <span>{relatedPost.author || "Admin"}</span>
                      <span>
                        {relatedPost.read_time || 5} {t("blog.minRead")}
                      </span>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Navigation */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-4xl mx-auto text-center">
            <button
              onClick={() => navigate(`/${currentLanguage}/blogs`)}
              className={`px-8 py-3 rounded-none font-light tracking-wide transition-colors ${isDark
                  ? "bg-gray-700 text-gray-100 hover:bg-gray-600"
                  : "bg-gray-900 text-white hover:bg-gray-800"
                }`}
            >
              {t("blog.backToAllArticles")}
            </button>
          </div>
        </motion.section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default BlogDetail;
